import { cloneDeep } from "lodash"
import { useTranslation } from "react-i18next"
import { useMemo } from "react"

import { SectionStats, TableHeaderColumn } from "@/app/report/common/constructor"
import {
  COLUMN_LEN_LIMIT,
  ROW_LEN_LIMIT,
  ROW_LEN_LIMIT_FIRST_PAGE,
} from "@/app/report/common/export/constants"
import PrintableSection from "@/app/report/components/section/pdf/PrintableSection"
import { getSplittedByPage } from "@/app/report/common/export/utils"
import { DataTable, TableRow } from "@/app/report/common/type"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { REPORT_TYPE } from "@/app/report/common/constants"
import PrintableTableChunk from "@/app/report/components/section/pdf/PrintableTableChunk"

import "./printableTable.scss"

// 표 형태 섹션의 PDF 렌더러 (컬럼/행 분할·스케일). 원래 Stats by Activity 용이었고 Stats 폐지(#99) 후에는 Custom Table PDF 가 사용한다.
export default function PrintableTableSection({
  data,
  scaleRatio = 1,
  currentDecimalPoint = 1,
  reportType,
  // 상단에 합계/평균 2행이 있는지 — 행 음영(zebra) 패리티 반전과 페이지 분할 행수 보정의 기준.
  // daily Stats 는 있고, Custom Table 은 요약 행(#98)이 만들어졌을 때만 호출부가 true 로 넘긴다 (WEB-2271 음영 회귀 수정)
  hasSummaryRow = reportType === "daily",
  isFirst = false,
}: {
  data: SectionStats
  scaleRatio: number
  currentDecimalPoint?: number
  reportType: REPORT_TYPE
  hasSummaryRow?: boolean
  isFirst?: boolean
}) {
  const { t } = useTranslation()

  const extendedData = cloneDeep(data) as SectionStats
  const translateKey = useTranslateMetricKey()

  const splittedTableData = useMemo(() => {
    if (!data || !data.table) {
      return []
    }

    // 원본 데이터 변형 방지: tableHeader/tableBody 복사본 사용
    const {
      table: { tableBody, tableHeader },
    } = extendedData

    const trendPdfOffset = 7
    const spliceCount = 2

    // 복사본을 만들어서 splice 대신 slice 사용
    let headerCopy = [...tableHeader]
    const bodyCopy = tableBody.map((row) => [...row])

    const splittedTableBody: DataTable[][] = []
    const splittedTableHeader: TableHeaderColumn[][] = []
    const defaultTableHeader: TableHeaderColumn[] = headerCopy.slice(0, spliceCount)
    headerCopy = headerCopy.slice(spliceCount)

    while (headerCopy.length > 0) {
      const tableHeaderList = headerCopy.slice(0, COLUMN_LEN_LIMIT)
      splittedTableHeader.push([...defaultTableHeader, ...tableHeaderList])
      headerCopy = headerCopy.slice(COLUMN_LEN_LIMIT)
    }

    let maxLine = 0
    splittedTableHeader.forEach((headers) => {
      const CELL_MAX_WIDTH = 63
      const CELL_FONT_SIZE = 10
      const CELL_PADDING = 8
      const CELL_HEADER_LINE_HEIGHT = 13
      headers.forEach((header) => {
        const div = document.createElement("div")
        div.style.maxWidth = scaleRatio * CELL_MAX_WIDTH + "px"
        div.style.wordBreak = "keep-all"
        div.style.fontSize = scaleRatio * CELL_FONT_SIZE + "px"
        div.style.padding = "0 " + scaleRatio * CELL_PADDING + "px"
        div.innerHTML = translateKey(header.title) as string

        document.body.appendChild(div)
        maxLine = Math.max(
          maxLine,
          Math.round(div.clientHeight / (CELL_HEADER_LINE_HEIGHT * scaleRatio)),
        )
        document.body.removeChild(div)
      })
    })

    // 날짜와 이름을 추출 (복사본)
    const defaultTableBody: TableRow[] = bodyCopy.map(
      (row: TableRow) => row.slice(0, spliceCount) as TableRow,
    )
    let bodyRest = bodyCopy.map((row) => row.slice(spliceCount))
    for (let i = 0; bodyRest.length > 0; i++) {
      let rowLenLimit
      if (i === 0 && isFirst) {
        rowLenLimit = ROW_LEN_LIMIT_FIRST_PAGE + trendPdfOffset
      } else {
        rowLenLimit = ROW_LEN_LIMIT - 1 - Math.max(0, maxLine - 2)
      }
      // 로우 제한만큼 자름
      const tableData = bodyRest.slice(0, rowLenLimit)
      const defaultData = defaultTableBody.slice(0, rowLenLimit)
      // bodyRest에서 잘라낸 부분 제거
      bodyRest = bodyRest.slice(rowLenLimit)
      // defaultTableBody도 동일하게 잘라냄
      defaultTableBody.splice(0, rowLenLimit)

      const result = []
      // 컬럼 제한 만큼 자름
      let tempTableData = tableData.map((row) => [...row])
      while (tempTableData.some((row) => row.length > 0)) {
        const splitted = tempTableData.map((data) => data.slice(0, COLUMN_LEN_LIMIT))
        tempTableData = tempTableData.map((row) => row.slice(COLUMN_LEN_LIMIT))
        // 고정 컬럼을 붙임
        result.push(defaultData.map((row, index) => row.concat(splitted[index]) as TableRow))
      }
      splittedTableBody.push(result)
    }

    return getSplittedByPage({
      splittedTableBody,
      splittedTableHeader,
      maxLine,
      isDaily: hasSummaryRow,
      isFirst: isFirst,
      data: data as SectionStats,
    }) as SectionStats[][]
  }, [data])

  return (
    <div className="renewal-pdf-container renewal-pdf table-container preview StatsTablePdfCont">
      {/* 제목은 첫 테이블의 caption 으로 렌더 — 테이블 분할 캡처(extracted-individual-table)가
          .section-name(테이블 밖)을 제외해 PDF 에 제목이 빠지던 문제 (WEB-2273) */}
      <PrintableSection hideHeader scaleRatio={scaleRatio}>
        {splittedTableData.flat().map((section: SectionStats, index: number) => {
          // 컬럼 수 비례 폭: 풀 청크는 전폭(100%), 분할 마지막 청크는 같은 컬럼 밀도만큼만 (WEB-2276)
          const maxColumns = Math.max(
            ...splittedTableData.flat().map((chunk) => chunk.table.tableHeader?.length || 0),
          )
          const columnRatio = maxColumns ? (section.table.tableHeader?.length || 0) / maxColumns : 1

          return (
            <PrintableTableChunk
              key={`table_section_${index}`}
              tableHeader={section.table.tableHeader}
              tableBody={section.table.tableBody}
              sort={extendedData.table.sort}
              hasSummaryRow={hasSummaryRow}
              currentDecimalPoint={currentDecimalPoint}
              style={{ width: `${Math.min(100, columnRatio * 100)}%` }}
              caption={
                index === 0
                  ? { title: t(data.title), countText: `1 / ${splittedTableData.length}` }
                  : undefined
              }
            />
          )
        })}
      </PrintableSection>
    </div>
  )
}
